// Angular
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { catchError, filter, mergeMap, tap } from 'rxjs/operators';
import { merge, Observable, Subject, Subscription, timer } from 'rxjs';
// Afidium
import { ViewsService } from 'libraries/view/views.service';
import { AppRootService } from 'app/shared/services/app-root/app-root-service';
import { ISearchStatus } from "../../shared/interfaces/ISearchStatus";
import { ActivatedRoute } from "@angular/router";

import { WORDING } from "./config/wording";
import { SashaPageComponent } from '../sasha-page.component';
import { ProcessingResourceService } from 'app_sasha/shared/resources/processings/processingResource.service';
import { SashaBreadcrumbService } from 'app_sasha/shared/components/breadcrumb/sasha-breadcrumb.service';
import { ILabels } from 'libraries/interfaces/ILabels';
import { IProcessing } from 'app_sasha/shared/resources/processings/IProcessing';
import { TitleService } from 'app_sasha/shared/services/title/title.service';
import { Yii } from 'libraries/interfaces/Yii';
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { IPageChange, IPaginationRecordset } from "libraries/component/pagination/IPagination";

export interface IIdRouteParameters {
    id: string;
}

const REFRESH_DELAY_MS: number = 10e3;

@Component({
    selector: 'processing-detail',
    template: `
        <div class="app-wrapper app-sasha">
            <sasha-breadcrumb></sasha-breadcrumb>
            <card *ngIf="loadStatus.isError" mode='bodyOnly'>
                <div class="card-body-content">
                    <notification type="error">{{loadStatus.errorMessage}}</notification>
                </div>
            </card>
            <card *ngIf="loadStatus.isLoading" mode='bodyOnly'>
                <div class="card-body-content">
                    <loader [message]="wording.loadingMessage"></loader>
                </div>
            </card>

            <processing-info *ngIf="!loadStatus.isLoading && processing"
                             [processing]="processing"
                             [select]="select"
                             [selectedStatus]="selectedStatus"
                             (onRefresh)="refreshManually()"
                             (onSelect)="reloadWithStatus($event)"></processing-info>

            <processing-task-list *ngIf="!loadStatus.isLoading && processing"
                                  [tasks]="processing.task"
                                  [processingId]="processing.id"
                                  [processingType]="processing.type"
                                  [recordset]="recordset"
                                  (onRetry)="refresh()"
                                  (onPageChange)="pageChange($event)"></processing-task-list>
        </div>
    `,
    styles: [`
        loader {
            background: white;
        }
    `]
})
export class ProcessingDetailComponent extends SashaPageComponent implements OnInit, OnDestroy {
    public loadStatus: ISearchStatus;
    public processing: IProcessing;
    public wording: ILabels = WORDING;

    public filter?: string = null;
    public select: boolean = true;
    public selectedStatus: string;

    public recordset: IPaginationRecordset = {total: 500, first: 1};
    public nbrecord: number = 20;

    private id: string = undefined;

    private readonly subscription: Subscription = new Subscription();
    private readonly refreshLauncher: Subject<void> = new Subject();
    private readonly manualRefreshLauncher: Subject<void> = new Subject();


    constructor(
        protected views: ViewsService,
        appRootService: AppRootService,
        private readonly route: ActivatedRoute,
        private readonly processingResourceService: ProcessingResourceService,
        private readonly sashaBreadcrumbService: SashaBreadcrumbService,
        private readonly titleService: TitleService,
        private readonly errorTracingService: ErrorTracingService,
    ) {
        super(views, appRootService);
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.loadStatus = {
            isLoading: false,
            isError: false
        };

        this.titleService.setTitle(Yii.t("wui-sasha-js", "Traitement"));

        this.subscription.add(
            merge(
                this.route.params
                    .pipe(
                        tap((params: IIdRouteParameters): void => {
                            this.id = params.id;
                            this.setBreadCrumb(params.id);
                            this.loadStatus = this.getLoadStatus({isLoading: true});
                        })
                    ),
                this.refreshLauncher,
                this.manualRefreshLauncher
                    .pipe(
                        tap(() => {
                            this.loadStatus = this.getLoadStatus({isLoading: true});
                        })
                    ),
                timer(REFRESH_DELAY_MS, REFRESH_DELAY_MS)
                    .pipe(
                        filter((): boolean => !this.loadStatus || !this.loadStatus.isLoading),
                        filter((): boolean => Boolean(
                            this.processing && this.processing.status && (this.processing.status.pending || this.processing.status.processing))
                        ),
                    ),
            )
                .pipe(
                    filter(() => this.id !== undefined),
                    mergeMap((): Observable<IProcessing> => this.processingResourceService.get(this.id)),
                    tap((processing: IProcessing) => {
                        this.loadStatus = this.getLoadStatus(
                            {isLoading: false, isError: false}
                        );
                        this.processing = processing;
                        this.recordset = processing.recordset || {first: 1, nbrecord: 50, total: 253};
                        this.titleService.setTitle(this.processing.title, Yii.t("wui-sasha-js", "Traitement"));
                    }),
                    catchError(async (error: Error) => {
                        this.errorTracingService.captureException(error);
                        this.loadStatus = this.getLoadStatus(
                            {isLoading: false, isError: true, errorMessage: error.message}
                        );
                    })
                ).subscribe({
                error: (error: Error): void => {
                    this.errorTracingService.captureException(error);
                }
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.manualRefreshLauncher.complete();
        this.refreshLauncher.complete();
    }

    public refresh(): void {
        this.refreshLauncher.next();
    }

    public refreshManually(): void {
        this.manualRefreshLauncher.next();
    }

    public onSelect(name: string): void {
        this.filter = this.filter === name ? null : name;
    }

    public pageChange(change: IPageChange): void {
        this.loadStatus = this.getLoadStatus({isLoading: true});
        this.processingResourceService.get(this.id, change.first + 1, change.rows, this.selectedStatus)
            .subscribe(
                {
                    next: (processing: IProcessing): void => {
                        this.reloadNext(processing);
                    },
                    error: (error: Error): void => {
                        this.reloadError(error);
                    }
                }
            );
    }

    public reloadWithStatus(status: string): void {
        this.selectedStatus = status && status !== this.selectedStatus ? status : null;
        this.loadStatus = this.getLoadStatus({isLoading: true});

        this.processingResourceService.get(this.id, 1, null, this.selectedStatus)
            .subscribe(
                {
                    next: (processing: IProcessing): void => {
                        this.reloadNext(processing);
                    },
                    error: (error: Error): void => {
                        this.reloadError(error);
                    }
                }
            );

    }

    private reloadNext(processing: IProcessing): void {
        this.loadStatus = this.getLoadStatus(
            {isLoading: false, isError: false}
        );
        this.processing = processing;
        this.recordset = processing.recordset || {first: 1, nbrecord: 50, total: 253};
        if(this.selectedStatus){
            this.recordset = {
                ...this.recordset,
                total : this.processing.status[this.selectedStatus]
            };
        }
    }

    private reloadError(error: Error): void {
        this.errorTracingService.captureException(error);
        this.loadStatus = this.getLoadStatus(
            {isLoading: false, isError: true, errorMessage: error.message}
        );
        this.errorTracingService.captureException(error);
    }

    private getLoadStatus(newStatus: ISearchStatus): ISearchStatus {
        return {
            ...this.loadStatus,
            ...newStatus
        };
    }

    private setBreadCrumb(id: string): void {
        this.sashaBreadcrumbService.setProcessing(id);
    }
}
